import type { DateTime } from 'luxon';
import { useCallback, useEffect, useRef, useState } from 'react';

import { CALENDAR_VIEW_CONSTANTS } from '../types';

const { HOUR_HEIGHT, HOURS_IN_DAY } = CALENDAR_VIEW_CONSTANTS;

/** Snap interval in minutes */
const SNAP_MINUTES = 15;

/** Snap interval in pixels */
const SNAP_PX = (SNAP_MINUTES / 60) * HOUR_HEIGHT;

/** Maximum Y position (bottom of 24h grid) */
const MAX_Y = HOURS_IN_DAY * HOUR_HEIGHT;

export interface DragSelection {
  date: DateTime;
  startMinutes: number;
  durationMinutes: number;
}

export interface DragSelectionPopoverAnchor {
  top: number;
  left: number;
}

interface DragState {
  isDragging: boolean;
  dayDate: DateTime | null;
  startY: number;
  currentY: number;
}

const initialDragState: DragState = {
  isDragging: false,
  dayDate: null,
  startY: 0,
  currentY: 0,
};

function snapToGrid(y: number): number {
  return Math.round(y / SNAP_PX) * SNAP_PX;
}

function clampY(y: number): number {
  return Math.max(0, Math.min(y, MAX_Y));
}

function pxToMinutes(y: number): number {
  return (y / HOUR_HEIGHT) * 60;
}

export interface UseDragSelectionResult {
  dragState: DragState;
  handleMouseDown: (e: React.MouseEvent, dayDate: DateTime) => void;
  selection: DragSelection | null;
  popoverAnchor: DragSelectionPopoverAnchor | null;
  dismissPopover: () => void;
  clearSelection: () => void;
}

export function useDragSelection(
  scrollContainerRef: React.RefObject<HTMLDivElement | null>,
): UseDragSelectionResult {
  const [dragState, setDragState] = useState<DragState>(initialDragState);
  const [popoverAnchor, setPopoverAnchor] = useState<DragSelectionPopoverAnchor | null>(null);
  const [selection, setSelection] = useState<DragSelection | null>(null);

  // Use ref to access latest drag state in document-level listeners
  const dragRef = useRef(dragState);
  dragRef.current = dragState;

  const dismissPopover = useCallback(() => {
    setPopoverAnchor(null);
  }, []);

  const clearSelection = useCallback(() => {
    setDragState(initialDragState);
    setPopoverAnchor(null);
    setSelection(null);
  }, []);

  const getYInGrid = useCallback((clientY: number, dayColumnEl: HTMLElement): number => {
    const rect = dayColumnEl.getBoundingClientRect();
    return clampY(clientY - rect.top);
  }, []);

  // Store reference to the day column element during drag
  const dayColumnRef = useRef<HTMLElement | null>(null);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, dayDate: DateTime) => {
      // Only left mouse button
      if (e.button !== 0) return;

      // Don't start drag if clicking on an event block
      const target = e.target as HTMLElement;
      if (target !== e.currentTarget && !target.classList.contains('time_grid__hour_cell')) {
        // Allow drag from hour cells but not from event blocks
        const closestEvent = target.closest('[class*="event_block"]');
        if (closestEvent) return;
      }

      e.preventDefault();

      const dayColumn = e.currentTarget as HTMLElement;
      dayColumnRef.current = dayColumn;

      const y = snapToGrid(getYInGrid(e.clientY, dayColumn));

      setDragState({
        isDragging: true,
        dayDate,
        startY: y,
        currentY: y,
      });
      setPopoverAnchor(null);
      setSelection(null);
    },
    [getYInGrid],
  );

  // Document-level mousemove and mouseup handlers
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragRef.current.isDragging || !dayColumnRef.current) return;

      const rawY = getYInGrid(e.clientY, dayColumnRef.current);
      const snappedY = snapToGrid(rawY);

      // Only allow dragging downward (currentY >= startY), enforce minimum 1 snap step
      const newY = Math.max(snappedY, dragRef.current.startY + SNAP_PX);

      setDragState((prev) => ({
        ...prev,
        currentY: newY,
      }));
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (!dragRef.current.isDragging || !dragRef.current.dayDate) return;

      const { startY, currentY, dayDate } = dragRef.current;
      const height = currentY - startY;

      if (height >= SNAP_PX) {
        // Valid selection
        const startMinutes = pxToMinutes(startY);
        const durationMinutes = pxToMinutes(height);

        setSelection({
          date: dayDate,
          startMinutes,
          durationMinutes,
        });

        setPopoverAnchor({
          top: e.clientY,
          left: e.clientX,
        });
      }

      setDragState((prev) => ({
        ...prev,
        isDragging: false,
      }));
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [getYInGrid]);

  // Escape key handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (dragRef.current.isDragging || popoverAnchor !== null) {
          clearSelection();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [popoverAnchor, clearSelection]);

  return {
    dragState,
    handleMouseDown,
    selection,
    popoverAnchor,
    dismissPopover,
    clearSelection,
  };
}
